import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, message } from 'antd';
import { FT170 } from '../../services/t207Api';
import { useT207Mode } from './hooks/useT207Mode';
import { useLookupNames } from './hooks/useLookupNames';
import { useT207Record } from './hooks/useT207Record';
import { useT207Navigation } from './hooks/useT207Navigation';
import { useModulePermission } from '../../hooks/usePermission';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2VendorPeople from './components/Tab2VendorPeople';
import Tab3Agenda from './components/Tab3Agenda';
import ActionBar from './components/ActionBar';
import { dateToInput, inputToDate } from './helpers';
import './T207Form.css';

const T207Form: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedAttendeeIdx, setSelectedAttendeeIdx] = useState(0);
  const [selectedVendorIdx, setSelectedVendorIdx] = useState(0);
  const [selectedAgendaIdx, setSelectedAgendaIdx] = useState(0);

  // Hooks
  const perms = useModulePermission(Modules.T207);
  const { mode, setMode, isEditing } = useT207Mode();
  const { names, resolveAllNames, resolveSingleName, clearNames } = useLookupNames();
  const {
    record, formData, attendees, agendas, vendorPeople, loading,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    addAttendeeRow, deleteAttendeeRow, setAttendeeField,
    addAgendaRow, deleteAgendaRow, setAgendaField,
    addTrackingItem, deleteTrackingItem, setTrackingField,
    addVendorRow, deleteVendorRow, setVendorField,
    revertToRecord, resetForAdd, applyResult,
  } = useT207Record();

  // Wrap loadRecord to also resolve names
  const loadRecordWithNames = useCallback(async (id: string) => {
    const result = await loadRecord(id);
    if (result) {
      resolveAllNames(result.data);
    }
  }, [loadRecord, resolveAllNames]);

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = useT207Navigation(loadRecordWithNames);

  // Initial Load
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecordWithNames(ids[0]);
        }
      } catch {
        console.error('Failed to load T207 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadList, loadRecordWithNames, setCurrentIdx]);

  // Mode Actions
  const handleAddAction = useCallback(async () => {
    if (isEditing) {
      await handleSave(mode, async (result: FT170) => {
        if (mode === 'add') {
          const ids = await loadList();
          const newIdx = ids.indexOf(result.ft170_01);
          setCurrentIdx(newIdx >= 0 ? newIdx : 0);
        }
        applyResult(result);
        setMode('browse');
      });
    } else {
      setMode('add');
      resetForAdd();
      setActiveTab(0);
      clearNames();
    }
  }, [isEditing, mode, handleSave, loadList, setCurrentIdx, applyResult, setMode, resetForAdd, clearNames]);

  const handleModifyAction = useCallback(() => {
    if (isEditing) {
      revertToRecord();
      setMode('browse');
    } else {
      if (!record) return;
      setMode('edit');
    }
  }, [isEditing, record, revertToRecord, setMode]);

  const handleDeleteAction = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除會議記錄 ${record.ft170_01}？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const success = await handleDelete();
        if (success) {
          const ids = await loadList();
          if (ids.length > 0) {
            const nextIdx = Math.min(currentIdx, ids.length - 1);
            setCurrentIdx(nextIdx);
            await loadRecordWithNames(ids[nextIdx]);
          } else {
            resetForAdd();
            setCurrentIdx(-1);
          }
        }
      },
    });
  }, [record, isEditing, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecordWithNames, resetForAdd]);

  const handleNavTo = useCallback((idx: number) => {
    navTo(idx, isEditing);
  }, [navTo, isEditing]);

  const handlePrint = useCallback(() => {
    if (!record || isEditing) return;
    message.info('列印功能開發中');
  }, [record, isEditing]);

  // Render
  return (
    <PermissionGuard module={Modules.T207} action="query">
    <div className="t207-form">
      {loading && <div className="loading-overlay">載入中...</div>}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>首頁</a>
              <span>/</span>
              <a href="#/erp" onClick={e => { e.preventDefault(); navigate('/erp'); }}>廠商管理</a>
              <span>/</span>
              <span className="current">廠商會議記錄</span>
            </nav>
            <span className="title-separator">--</span>
            <h1>廠商會議記錄管理</h1>
            <span className="badge badge-default">T207</span>
          </div>
        </div>

        {/* Search Bar — Meeting code + subject + date */}
        <div className="search-bar">
          <div className="field-group">
            <label>會議代號</label>
            <input
              type="text"
              value={String(getField('ft170_01') || '')}
              disabled
              style={{ width: 140 }}
            />
          </div>
          <div className="field-group">
            <label>會議主題</label>
            <input
              type="text"
              value={String(getField('ft170_11') || '')}
              onChange={e => setField('ft170_11', e.target.value)}
              disabled={!isEditing}
              style={{ width: 280 }}
            />
          </div>
          <div className="field-group">
            <label>會議日期</label>
            <input
              type="date"
              value={dateToInput(formData.ft170_13)}
              onChange={e => setField('ft170_13', inputToDate(e.target.value) as string)}
              disabled={!isEditing}
              style={{ width: 160 }}
            />
          </div>
        </div>

        {/* Tabs (3 pages from ui_spec) */}
        <div className="tabs">
          <div className="tab-nav">
            {['基本資料', '廠商參加人員', '會議內容'].map((label, i) => (
              <button
                key={i}
                className={`tab-nav-item ${activeTab === i ? 'active' : ''}`}
                onClick={() => setActiveTab(i)}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Tab 1: 基本資料 + 與會人員 */}
          <div className={`tab-pane ${activeTab === 0 ? 'active' : ''}`}>
            <Tab1BasicInfo
              formData={formData}
              names={names}
              isEditing={isEditing}
              getField={getField}
              setField={setField}
              resolveSingleName={resolveSingleName}
              attendees={attendees}
              addAttendeeRow={addAttendeeRow}
              deleteAttendeeRow={deleteAttendeeRow}
              setAttendeeField={setAttendeeField}
              selectedAttendeeIdx={selectedAttendeeIdx}
              setSelectedAttendeeIdx={setSelectedAttendeeIdx}
            />
          </div>

          {/* Tab 2: 廠商參加人員 */}
          <div className={`tab-pane ${activeTab === 1 ? 'active' : ''}`}>
            <Tab2VendorPeople
              formData={formData}
              vendorPeople={vendorPeople}
              isEditing={isEditing}
              addVendorRow={addVendorRow}
              deleteVendorRow={deleteVendorRow}
              setVendorField={setVendorField}
              selectedVendorIdx={selectedVendorIdx}
              setSelectedVendorIdx={setSelectedVendorIdx}
            />
          </div>

          {/* Tab 3: 會議內容 — 議題 + 追蹤事項 */}
          <div className={`tab-pane ${activeTab === 2 ? 'active' : ''}`}>
            <Tab3Agenda
              formData={formData}
              agendas={agendas}
              isEditing={isEditing}
              addAgendaRow={addAgendaRow}
              deleteAgendaRow={deleteAgendaRow}
              setAgendaField={setAgendaField}
              addTrackingItem={addTrackingItem}
              deleteTrackingItem={deleteTrackingItem}
              setTrackingField={setTrackingField}
              selectedAgendaIdx={selectedAgendaIdx}
              setSelectedAgendaIdx={setSelectedAgendaIdx}
            />
          </div>
        </div>

        {/* Bottom Action Bar (Pwtcmd1) */}
        <ActionBar
          mode={mode}
          isEditing={isEditing}
          record={record}
          currentIdx={currentIdx}
          allIdsLength={allIds.length}
          permissions={perms}
          onAdd={handleAddAction}
          onModify={handleModifyAction}
          onDelete={handleDeleteAction}
          onPrint={handlePrint}
          onNavTo={handleNavTo}
          onExit={() => navigate('/erp')}
        />

      </div>
    </div>
    </PermissionGuard>
  );
};

export default T207Form;
